import React, { useState, useEffect } from 'react';
import { NavLink, useSearchParams } from "react-router-dom";
import moment from 'moment';
import axios from 'axios';
import styled from 'styled-components';
import LoggedLayout from 'layouts/LoggedLayout';
import PopupLayout from 'layouts/PopupLayout';
import Paginator from 'components/Paginator';
import ThSortable from 'components/ThSortable';
import TrSkeleton from 'components/TrSkeleton';
import { appendToSession, getFromSession } from 'helpers/session';

const Table = styled.table`
	th,td {
		@media (min-width: 768px) {
			&:nth-child(1),
			&:nth-child(3) {
				width: 300px;
			}
		
			&:nth-child(2) {
				width: 100px;
			}

			&:nth-child(6) {
				width: 40px;
			}
		}
		
		@media (max-width: 768px) {
			&:nth-child(4) {
				display: none;
			}
		}
	}
`;

let axiosCancelToken = null;
let searchTimeout = null;

export default function Contacts() {
	const [queryParams] = useSearchParams();
	const popup = queryParams.get('popup') === 'true';

	let filters = getFromSession('contacts-list');

	let [contacts, setContacts] = useState({});
	let [search, setSearch] = useState(filters?.search ?? undefined);
	let [sortDirection, setSortDirection] = useState(filters?.direction ?? 'asc');
	let [sortField, setSortField] = useState(filters?.sort ?? 'name');
	let [skeletonRows, setSkeletonRows] = useState(5);
	let [page, _setPage] = useState(queryParams.get('page') ?? (filters?.page ?? 1));
	const setPage = (page) => {
		setContacts({...contacts, data: undefined});
		_setPage(page);
	}

	const setSearchTimeout = (value) => {
		if ( searchTimeout ) clearTimeout(searchTimeout);
		searchTimeout = setTimeout(() => {
			setSearch(value);
			setPage(1);
		}, 1000);
	}

	const sortTableClick = (field) => {
		if ( !field ) return;
		if ( field === sortField ) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
		setSortField(field);
	};

	useEffect(() => {
		axiosCancelToken = axios.CancelToken.source();
	
		return function cleanup() {
           	axiosCancelToken.cancel();
        }
	}, []);

	useEffect(() => {
		const getContacts = async () => {
			let params = {
				page: page,
				search: search,
				sort: sortField,
				direction: sortDirection,
			};

			appendToSession('contacts-list', params);

			await axios.get('/api/manager/contacts/list', {
				params: params,
				cancelToken: axiosCancelToken.token
			}).then((response) => {
		    	setContacts({...response.data});
		    	setSkeletonRows(response.data.data.length);
		  	}).catch((error) => {
				if ( axios.isCancel(error) ) return;
			});	
		}

		getContacts();
	}, [page, search, sortField, sortDirection]);

	const Layout = popup ? PopupLayout : LoggedLayout;

	return (
		<Layout>
			<section>
				<div className="page-title">
					<h1>Contactos</h1>
					
					<NavLink to={'/contacts/add' + (popup ? '?popup=true' : '')} className="btn btn-sm btn-light ms-auto">Nuevo contacto</NavLink>
				</div>

				<div className="page-content">
					<div className="card border-0 shadow-sm">
						<div className="card-header bg-white p-3">
							<div className="row">
								<div className="col-md-3 mb-2 mt-2 mb-md-0 mt-md-0">
									<input type="search" className="form-control form-control-sm" placeholder="Buscar" defaultValue={search ?? ''} onChange={(e) => setSearchTimeout(e.target.value)} />
								</div>
								<div className="col-md-2 mb-2 mt-2 mb-md-0 mt-md-0">
									
								</div>
								<div className="col-md-2 mb-2 mt-2 mb-md-0 mt-md-0">
									
								</div>
							</div>
						</div>
						<div className="card-body p-0">
							<div className="table-responsive table-responsive-carded">
								<Table className="table table-hover table-sortable table-carded">
									<thead>
										<tr>
											<ThSortable direction={sortDirection} active={sortField === 'name'} onClick={() => sortTableClick('name')}>Nombre</ThSortable>
											<ThSortable direction={sortDirection} active={sortField === 'vatnumber'} onClick={() => sortTableClick('vatnumber')}>CIF</ThSortable>
											<ThSortable direction={sortDirection} active={sortField === 'created_at'} onClick={() => sortTableClick('created_at')}>Añadido el</ThSortable>
											<th style={{width: '40px'}}></th>
										</tr>
									</thead>
									<tbody>
										{ contacts.data?.length > 0 &&
											contacts.data.map((el, idx) => {
												return ( 
													<React.Fragment key={'c'+idx}>
														<tr>
															<td>{el.name ?? '-'}</td>
															<td>{el.vatnumber ?? '-'}</td>
															<td>
																{ moment(el.created_at).format('DD-MM-YYYY') }
																&nbsp;<small>{ moment(el.created_at).format('HH:mm') }</small>
															</td>
															<td>
																<NavLink className="btn-table" to={'/contacts/edit/' + el.id + (popup ? '?popup=true' : '')}><i className="bi bi-pencil"></i></NavLink>
															</td>
														</tr>
													</React.Fragment>
												);
											})
										}

										{ contacts.data && !contacts.data.length && <tr><td colSpan="100%">No hay datos disponibles</td></tr> }

										{ contacts.data === undefined && <TrSkeleton rows={skeletonRows} columns={5} /> }
									</tbody>
								</Table>
							</div>
						</div>
						<div className="card-footer p-3 d-flex justify-content-end" id={popup ? 'footer-fixed' : ''}>
							<div className="d-inline-block">
								<Paginator
									min={1}
									current={contacts?.current_page}
									max={contacts?.last_page}
									changeCallback={(page) => setPage(page)}
								/>
							</div>
						</div>
					</div>
				</div>
			</section>
		</Layout>
	);
}


